/**
 * NumberingCell — table-cell editor for a document type's numbering config
 * (format mask + base date field + reset policy), for the inline-editable
 * document-types tables (see `docs/refactor/SAVEAGGREGATE-ROLLOUT.md` sibling
 * cell-editor pattern established by `InlineEditableCell`).
 *
 * Unlike `InlineEditableCell` (single scalar field, inline `<input>`), this
 * cell edits THREE fields together as one atomic commit, so it hosts the
 * existing stateless `NumberingMaskEditor` inside a `CellPopover` with its own
 * local draft + explicit Zatwierdź/Anuluj buttons rather than committing on
 * every keystroke/blur.
 *
 * Async error-recovery mirrors `useInlineCellEdit`: `onCommit` may return a
 * Promise — while pending the editor + both buttons are disabled; on reject
 * the popover STAYS OPEN and shows the rejection message inline (user can fix
 * and retry); on resolve the popover closes. Returning void closes immediately
 * (sync fire-and-forget).
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { CellPopover } from '@/components/additional/CellPopover';
import { Button } from '@/components/Button';
import { NumberingMaskEditor } from '@/components/documents/NumberingMaskEditor';
import type { NumberingBaseFieldOption } from '@/components/documents/useNumberingBaseFieldOptions';

export interface NumberingCellValue {
  numberingFormat: string;
  numberingBaseField: string;
  numberingReset: 'continuous' | 'yearly' | 'monthly';
}

export interface NumberingCellProps {
  /** Kod typu dokumentu — przekazywany do `NumberingMaskEditor` dla podglądu. */
  code: string;
  value: NumberingCellValue;
  /**
   * Opcje pola daty numeracji (core + ext) budowane RAZ w rodzicu tabeli
   * (`useNumberingBaseFieldOptions`) i współdzielone przez wszystkie wiersze —
   * zamiast wołać hook per komórkę. WYMAGANE — przekazywane bezpośrednio do
   * `NumberingMaskEditor`, który też wymaga tej listy (patrz jego props).
   */
  baseFieldOptions: NumberingBaseFieldOption[];
  /**
   * Wywoływane po kliknięciu Zatwierdź. Może zwrócić Promise: reject zostawia
   * popover otwarty + pokazuje komunikat, resolve zamyka popover. Zwrot void
   * zamyka od razu (fire-and-forget).
   */
  onCommit: (next: NumberingCellValue) => void | Promise<void>;
  disabled?: boolean;
  'data-testid'?: string;
}

/** Narrow an unknown onCommit return to a thenable (async commit path). */
function isPromiseLike<T>(x: unknown): x is PromiseLike<T> {
  return (
    typeof x === 'object' && x !== null && typeof (x as { then?: unknown }).then === 'function'
  );
}

export function NumberingCell({
  code,
  value,
  baseFieldOptions,
  onCommit,
  disabled,
  'data-testid': testId,
}: NumberingCellProps) {
  const { t } = useTranslation();
  const anchorRef = useRef<HTMLButtonElement>(null);
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState<NumberingCellValue>(value);
  const [committing, setCommitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Guards a re-entrant Zatwierdź click (or close-while-pending) while an
  // async onCommit is still in flight — mirrors useInlineCellEdit's guard.
  const committingRef = useRef(false);
  // Avoid setState after unmount if an async onCommit settles late.
  const isMountedRef = useRef(true);
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const handleOpen = useCallback(() => {
    if (disabled) return;
    setDraft(value);
    setError(null);
    setOpen(true);
  }, [disabled, value]);

  // Anuluj / Escape / click-outside (CellPopover onClose): discard draft,
  // no onCommit. Frozen while an async commit is pending — its result would
  // otherwise land on a closed popover and the error could not be shown.
  const handleCancel = useCallback(() => {
    if (committingRef.current) return;
    setOpen(false);
    setError(null);
  }, []);

  const handleConfirm = useCallback(() => {
    if (committingRef.current) return;

    // No-op skip (spójnie z InlineEditableCell.isEqual): brak zmiany = tylko
    // zamknij, bez PUT/toast/audytu.
    if (
      draft.numberingFormat === value.numberingFormat &&
      draft.numberingBaseField === value.numberingBaseField &&
      draft.numberingReset === value.numberingReset
    ) {
      setError(null);
      setOpen(false);
      return;
    }

    const result = onCommit(draft);
    if (isPromiseLike<void>(result)) {
      committingRef.current = true;
      setCommitting(true);
      void Promise.resolve(result).then(
        () => {
          committingRef.current = false;
          if (!isMountedRef.current) return;
          setCommitting(false);
          setError(null);
          setOpen(false);
        },
        (reason: unknown) => {
          committingRef.current = false;
          if (!isMountedRef.current) return;
          setCommitting(false);
          setError(reason instanceof Error ? reason.message : String(reason));
        },
      );
    } else {
      setError(null);
      setOpen(false);
    }
  }, [draft, value, onCommit]);

  const editorTestIdPrefix = testId ? `${testId}-editor` : undefined;

  return (
    <>
      <button
        type="button"
        ref={anchorRef}
        onClick={handleOpen}
        disabled={disabled}
        aria-haspopup="dialog"
        aria-expanded={open}
        data-testid={testId}
        className={[
          'w-full truncate rounded px-2 py-1 text-left font-mono text-sm text-text-primary hover:bg-bg-tertiary disabled:cursor-not-allowed disabled:opacity-50',
          open ? 'bg-bg-tertiary ring-2 ring-accent-500' : '',
        ]
          .filter(Boolean)
          .join(' ')}
      >
        {value.numberingFormat || '—'}
      </button>
      <CellPopover
        open={open}
        anchorRef={anchorRef}
        onClose={handleCancel}
        ariaLabel={t('tenant.documentTypes.form.numberingFormat')}
        data-testid={testId ? `${testId}-popover` : undefined}
      >
        <div className="w-80 space-y-3 p-3">
          <NumberingMaskEditor
            code={code}
            numberingFormat={draft.numberingFormat}
            numberingBaseField={draft.numberingBaseField}
            baseFieldOptions={baseFieldOptions}
            numberingReset={draft.numberingReset}
            onFormatChange={(v) => {
              setError(null);
              setDraft((d) => ({ ...d, numberingFormat: v }));
            }}
            onBaseFieldChange={(v) => {
              setError(null);
              setDraft((d) => ({ ...d, numberingBaseField: v }));
            }}
            onResetChange={(v) => {
              setError(null);
              setDraft((d) => ({ ...d, numberingReset: v }));
            }}
            disabled={committing}
            testIdPrefix={editorTestIdPrefix}
          />
          {error != null && (
            <p
              role="alert"
              data-testid={testId ? `${testId}-error` : undefined}
              className="text-xs text-red-600 dark:text-red-400"
            >
              {error}
            </p>
          )}
          <div className="flex justify-end gap-2">
            <Button
              type="button"
              variant="secondary"
              size="sm"
              onClick={handleCancel}
              disabled={committing}
              data-testid={testId ? `${testId}-cancel` : undefined}
            >
              {t('common.cancel')}
            </Button>
            <Button
              type="button"
              variant="primary"
              size="sm"
              onClick={handleConfirm}
              loading={committing}
              data-testid={testId ? `${testId}-confirm` : undefined}
            >
              {t('common.confirm')}
            </Button>
          </div>
        </div>
      </CellPopover>
    </>
  );
}
